// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: how tall the Rail's panels are. Compact matches the icon strip, Tall
// is a fixed comfortable height, Full takes the screen's height. Every panel
// uses the same height so switching panels never resizes the Rail. Kept on this
// computer and shared with the main window's Settings through the storage event.

import {getProtectedLocalStorage} from '@app/features/platform/state/ProtectedWebStorage';
import {makeAutoObservable} from 'mobx';

export type RailSize = 'compact' | 'tall' | 'full';

export const RAIL_SIZES: ReadonlyArray<RailSize> = ['compact', 'tall', 'full'];

const STORAGE_KEY = 'konvo_rail.size';
const DEFAULT_SIZE: RailSize = 'tall';
/** Tall panels are this high, or the screen's height when that is less. */
export const TALL_PANEL_PX = 720;

function read(): RailSize {
	try {
		const value = getProtectedLocalStorage()?.getItem(STORAGE_KEY);
		return RAIL_SIZES.includes(value as RailSize) ? (value as RailSize) : DEFAULT_SIZE;
	} catch {
		return DEFAULT_SIZE;
	}
}

/** The panel height for a size: the strip's own height, a fixed height, or the whole screen. */
export function railPanelHeight(size: RailSize, stripHeight: number, fullHeight: number): number {
	switch (size) {
		case 'compact':
			return Math.min(Math.max(stripHeight, 1), fullHeight);
		case 'tall':
			return Math.min(TALL_PANEL_PX, fullHeight);
		case 'full':
			return fullHeight;
	}
}

class RailSizeStore {
	size: RailSize = read();

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		if (typeof window !== 'undefined') {
			window.addEventListener('storage', (event) => {
				if (event.key === STORAGE_KEY) this.size = read();
			});
		}
	}

	set(size: RailSize): void {
		this.size = size;
		try {
			getProtectedLocalStorage()?.setItem(STORAGE_KEY, size);
		} catch {
			// Storage may be unavailable; the change still holds for this window.
		}
	}
}

export default new RailSizeStore();
